import React, { useEffect, useState } from "react";
import Breadcrump from "../../components/Breadcrump";
import wishlistAPI from "../../api/wishlistApi";
import { normalizeProduct } from "../../utils/normalizeProduct";
import Product from "../products/Card";

// ── Ornament Divider ──────────────────────────────────────────────────────────
const OrnamentDivider = () => (
  <div className="wl-divider" aria-hidden="true">
    <span className="wl-divider-line" />
    <span className="wl-divider-diamond" />
    <span className="wl-divider-line" />
  </div>
);

// ── Product Skeletons ─────────────────────────────────────────────────────────
const WishlistSkeletons = () => (
  <div className="wl-grid">
    {Array.from({ length: 4 }).map((_, i) => (
      <div className="wl-skeleton" key={i}>
        <div className="wl-skeleton-img" />
        <div className="wl-skeleton-body">
          <div className="wl-skeleton-line wl-skeleton-line--short" />
          <div className="wl-skeleton-line" />
          <div className="wl-skeleton-line wl-skeleton-line--price" />
        </div>
      </div>
    ))}
  </div>
);

// ── Empty / Error State ───────────────────────────────────────────────────────
const EmptyWishlist = ({ isError, message }) => (
  <div className="wl-empty">
    <div className="wl-empty-icon" aria-hidden="true">
      <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M32 56S8 40 8 22a12 12 0 0 1 24 0 12 12 0 0 1 24 0c0 18-24 34-24 34z"
          stroke="var(--color-accent-main)"
          strokeWidth="1.2"
          strokeLinejoin="round"
        />
        <circle cx="32" cy="22" r="3" fill="var(--color-accent-main)" opacity="0.4" />
      </svg>
    </div>
    <h3 className="wl-empty-heading">
      {isError ? "Oops" : "Your wishlist awaits"}
    </h3>
    <p className="wl-empty-sub">
      {message ||
        "Save the pieces that speak to your soul — your curated collection lives here."}
    </p>
    {!isError && (
      <a href="/shop" className="wl-empty-btn">
        Explore Collection
        <svg className="wl-empty-arrow" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M1 6.5h11M7 1l5.5 5.5L7 12" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </a>
    )}
  </div>
);

// ── Main Component ────────────────────────────────────────────────────────────
const Wishlist = () => {
  // ── All logic is IDENTICAL to original ────────────────────────────────────
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchWishList(1);
  }, []);

  const fetchWishList = async (pageNum = 1) => {
    setLoading(true);

    try {
      const res = await wishlistAPI.listing({ page: pageNum });

      if (res.code === 200) {
        const { products, pagination } = res.data;

        const items = products;

        setProducts((prev) => (pageNum === 1 ? items : [...prev, ...items]));
        setTotalCount(pagination.totalCount);
        setHasMore(pageNum * 6 < totalCount);
      } else {
        setError("No Product found in wishlist.");
      }
    } catch (err) {
      setError("You have to log in to view your wishlist.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const onScroll = () => {
      if (
        !loading &&
        hasMore &&
        window.innerHeight + window.scrollY >= document.body.offsetHeight - 200
      ) {
        setPage((p) => p + 1);
      }
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [loading, hasMore]);

  useEffect(() => {
    if (page > 1) fetchWishList(page);
  }, [page]);
  // ── Logic ends ─────────────────────────────────────────────────────────────

  return (
    <>
      <Breadcrump title="Wishlist" />

      <main className="wl-root" aria-label="Your Wishlist">
        {/* ── Section Header ── */}
        <div className="wl-header">
          <p className="wl-eyebrow">
            <span className="wl-eyebrow-line" aria-hidden="true" />
            Saved For You
            <span className="wl-eyebrow-line" aria-hidden="true" />
          </p>
          <h1 className="wl-heading">
            My <em>Wishlist</em>
          </h1>
          <OrnamentDivider />
          {!loading && !error && products.length > 0 && (
            <p className="wl-count">
              {products.length} of {totalCount}{" "}
              {totalCount === 1 ? "piece" : "pieces"} saved
            </p>
          )}
        </div>

        <div className="container">
          {/* ── Error State ── */}
          {error && (
            <EmptyWishlist isError message={error} />
          )}

          {/* ── Loading — initial (no products yet) ── */}
          {loading && products.length === 0 && <WishlistSkeletons />}

          {/* ── Empty ── */}
          {!loading && !error && products.length === 0 && (
            <EmptyWishlist />
          )}

          {/* ── Product Grid ── */}
          {!error && products.length > 0 && (
            <div className="wl-grid">
              {products.map((product, index) => (
                <Product
                  key={product._id || index}
                  product={normalizeProduct(product)}
                />
              ))}

              {/* ── Loading more — inline skeletons at bottom ── */}
              {loading &&
                Array.from({ length: 4 }).map((_, i) => (
                  <div className="wl-skeleton" key={`skel-${i}`}>
                    <div className="wl-skeleton-img" />
                    <div className="wl-skeleton-body">
                      <div className="wl-skeleton-line wl-skeleton-line--short" />
                      <div className="wl-skeleton-line" />
                      <div className="wl-skeleton-line wl-skeleton-line--price" />
                    </div>
                  </div>
                ))}
            </div>
          )}

          {/* ── End of list ── */}
          {!hasMore && !loading && products.length > 6 && (
            <p className="wl-end-msg">
              <span className="wl-end-line" aria-hidden="true" />
              You've seen everything
              <span className="wl-end-line" aria-hidden="true" />
            </p>
          )}
        </div>
      </main>
    </>
  );
};

export default Wishlist;